/**
 * Charge Summary Module
 * Calculates and displays service charge summary
 *
 * NOTE: Anodes should NOT appear in charge summary - they only appear in assignment cards
 * Only status="replaced" anodes get charged to customer
 *
 * TODO: Extract full logic from index.html (currently stubbed)
 */

/**
 * Update the charge summary display
 * Fetches payment methods and delegates to adminApp.updateChargeSummary()
 * @param {Object} serviceData - Service data including items, charges, etc.
 */
export async function updateChargeSummary(serviceData) {
    // Always show charge summary if a service is selected
    const chargeSummary = document.querySelector('.charge-summary');
    if (window.currentServiceKey && chargeSummary) {
        chargeSummary.style.display = 'block';

        // Show end service button ONLY if Start Service has been clicked
        const endServiceContainer = document.getElementById('endServiceContainer');
        if (endServiceContainer && window.startTime) {
            endServiceContainer.style.display = 'block';
        }
    }

    // IMPORTANT: Display payment methods FIRST (before delegating)
    if (window.selectedCustomer && window.selectedCustomer.id) {
        // Use payment methods from selectedCustomer if available (from search results)
        if (window.selectedCustomer.payment_methods && window.selectedCustomer.payment_methods.length > 0) {
            await displayPaymentMethods(window.selectedCustomer.payment_methods);
        } else {
            // Fallback: fetch from API if customer has a real Stripe ID
            // Only call Stripe API if we have a valid Stripe customer ID (starts with cus_)
            const stripeCustomerId = window.selectedCustomer.stripe_customer_id;
            if (stripeCustomerId && stripeCustomerId.startsWith('cus_')) {
                await fetchAndDisplayPaymentMethods(stripeCustomerId);
            } else {
                // Customer doesn't have a Stripe account - show new customer message
                await fetchAndDisplayPaymentMethods(`supabase_${window.selectedCustomer.id}`);
            }
        }
    } else if (window.selectedCustomer) {
        console.warn('Customer selected but no ID:', window.selectedCustomer);
    }

    // THEN delegate to adminApp if it exists (it handles pricing calculations)
    if (window.adminApp && typeof window.adminApp.updateChargeSummary === 'function') {
        window.adminApp.updateChargeSummary();
        return;
    }

    if (!window.currentServiceKey) {
        if (chargeSummary) {
            chargeSummary.style.display = 'none';
        }
        return;
    }
}

/**
 * Calculate total charges from anode assignments
 * Only includes anodes marked as "replaced"
 */
export function calculateAnodeCharges() {
    if (!window.anodeAssignments) return 0;

    return Object.values(window.anodeAssignments)
        .filter(anode => anode.status === 'replaced' && anode.anode_price)
        .reduce((sum, anode) => sum + (parseFloat(anode.anode_price) || 0), 0);
}

/**
 * Display payment methods (used directly when already have data)
 * @param {Array} paymentMethods - Array of payment method objects
 */
async function displayPaymentMethods(paymentMethods) {

    // Find or create payment methods container in the payment method section
    let paymentMethodsContainer = document.getElementById('paymentMethodsContainer');

    if (!paymentMethodsContainer) {
        // Create container if it doesn't exist
        const paymentMethodSelector = document.querySelector('.payment-method-selector');
        if (paymentMethodSelector) {
            paymentMethodsContainer = document.createElement('div');
            paymentMethodsContainer.id = 'paymentMethodsContainer';
            paymentMethodsContainer.style.cssText = 'background: #f8f9fa; padding: 15px; border-radius: 8px; margin-bottom: 15px; border: 1px solid #dee2e6;';

            // Insert BEFORE the payment method selector (so it appears above the dropdown)
            paymentMethodSelector.parentNode.insertBefore(paymentMethodsContainer, paymentMethodSelector);
        }
    }

    if (!paymentMethodsContainer) {
        console.warn('Could not find or create payment methods container');
        return;
    }

    // Display payment methods
    if (paymentMethods.length === 0) {
        paymentMethodsContainer.innerHTML = `
            <h4 style="margin: 0 0 10px 0; color: #2c3e50; font-size: 16px;">💳 Payment Method</h4>
            <div style="padding: 10px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; color: #856404;">
                ⚠️ No payment methods on file. <br>
                <small>Customer will receive an invoice with payment options.</small>
            </div>
        `;
        window.selectedPaymentMethodId = null;
        window.hasPaymentMethod = false;

        // Update button text to "Invoice Customer"
        updateChargeButtonText(false);
        return;
    }

    // Build payment methods radio list
    let html = '<h4 style="margin: 0 0 10px 0; color: #2c3e50; font-size: 16px;">💳 Payment Method</h4>';
    html += '<div style="display: flex; flex-direction: column; gap: 8px;">';

    paymentMethods.forEach((pm, index) => {
        const brand = pm.card.brand.charAt(0).toUpperCase() + pm.card.brand.slice(1);
        const last4 = pm.card.last4;
        const expMonth = String(pm.card.exp_month).padStart(2, '0');
        const expYear = String(pm.card.exp_year).slice(-2);
        const isDefault = pm.is_default;
        const checked = isDefault || index === 0 ? 'checked' : '';

        // Auto-select default or first payment method
        if (checked) {
            window.selectedPaymentMethodId = pm.id;
        }

        html += `
            <label style="display: flex; align-items: center; padding: 10px; border: 2px solid ${checked ? '#3498db' : '#e0e0e0'}; border-radius: 6px; cursor: pointer; transition: all 0.2s;">
                <input type="radio" name="paymentMethod" value="${pm.id}" ${checked}
                       onchange="window.selectPaymentMethod('${pm.id}')"
                       style="margin-right: 10px; width: 18px; height: 18px;">
                <div>
                    <strong>${brand} •••• ${last4}</strong>
                    <small style="display: block; color: #666;">Exp: ${expMonth}/${expYear}</small>
                    ${isDefault ? '<span style="background: #27ae60; color: white; padding: 2px 6px; border-radius: 3px; font-size: 11px; margin-left: 8px;">DEFAULT</span>' : ''}
                </div>
            </label>
        `;
    });

    html += '</div>';

    paymentMethodsContainer.innerHTML = html;
    window.hasPaymentMethod = true;

    // Update button text to "Charge Customer"
    updateChargeButtonText(true);
}

/**
 * Fetch and display payment methods for a customer (fallback for when data not in customer object)
 * @param {string} customerId - Stripe customer ID
 */
async function fetchAndDisplayPaymentMethods(customerId) {
    try {
        // Check if this is a placeholder ID for customers without Stripe accounts
        if (customerId.startsWith('supabase_')) {
            console.log('ℹ️ Customer does not have a Stripe account yet - showing new customer message');
            const paymentMethodsContainer = document.getElementById('paymentMethodsContainer');
            if (paymentMethodsContainer) {
                paymentMethodsContainer.innerHTML = `
                    <h4 style="margin: 0 0 10px 0; color: #2c3e50; font-size: 16px;">💳 Payment Method</h4>
                    <div style="padding: 10px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; color: #856404;">
                        ℹ️ New customer - payment method will be collected at checkout
                    </div>
                `;
            }
            // Update button to show "Invoice Customer" instead of "Charge Customer"
            window.hasPaymentMethod = false;
            updateChargeButtonText(false);
            return;
        }

        // Determine API base URL
        const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
            ? 'http://localhost:3001/api'
            : '/api';

        // Fetch customer with payment methods
        const response = await fetch(`${API_BASE_URL}/stripe-customers?customerId=${customerId}`);
        if (!response.ok) {
            throw new Error('Failed to fetch customer payment methods');
        }

        const customer = await response.json();
        const paymentMethods = customer.payment_methods || [];

        // Use the shared display function
        await displayPaymentMethods(paymentMethods);

    } catch (error) {
        console.error('Error fetching payment methods:', error);

        const paymentMethodsContainer = document.getElementById('paymentMethodsContainer');
        if (paymentMethodsContainer) {
            paymentMethodsContainer.innerHTML = `
                <h4 style="margin: 0 0 10px 0; color: #2c3e50; font-size: 16px;">💳 Payment Method</h4>
                <div style="padding: 10px; background: #f8d7da; border: 1px solid #f5c6cb; border-radius: 4px; color: #721c24;">
                    ❌ Error loading payment methods. Please try again.
                </div>
            `;
        }
        // Treat error as no payment method - show Invoice button
        window.hasPaymentMethod = false;
        updateChargeButtonText(false);
    }
}

/**
 * Select a payment method
 * @param {string} paymentMethodId - Payment method ID
 */
function selectPaymentMethod(paymentMethodId) {
    window.selectedPaymentMethodId = paymentMethodId;

    // Update border colors for all payment method labels
    document.querySelectorAll('input[name="paymentMethod"]').forEach(radio => {
        const label = radio.closest('label');
        if (label) {
            label.style.borderColor = radio.checked ? '#3498db' : '#e0e0e0';
        }
    });
}

/**
 * Update charge button text based on payment method availability
 * Phase 8: Smart Button Logic
 * @param {boolean} hasPaymentMethod - Whether customer has payment method on file
 */
function updateChargeButtonText(hasPaymentMethod) {
    const chargeButton = document.getElementById('chargeButton');
    const pricingChargeButton = document.getElementById('pricingChargeButton');

    if (hasPaymentMethod) {
        // Customer has payment method - show "Charge Customer"
        if (chargeButton) {
            chargeButton.innerHTML = '💳 Charge Customer';
            chargeButton.title = 'Charge customer using saved payment method';
        }
        if (pricingChargeButton) {
            pricingChargeButton.innerHTML = '💳 Charge Customer';
            pricingChargeButton.title = 'Charge customer using saved payment method';
        }
    } else {
        // No payment method - show "Invoice Customer"
        if (chargeButton) {
            chargeButton.innerHTML = '📄 Invoice Customer';
            chargeButton.title = 'Send invoice to customer with payment options';
        }
        if (pricingChargeButton) {
            pricingChargeButton.innerHTML = '📄 Invoice Customer';
            pricingChargeButton.title = 'Send invoice to customer with payment options';
        }
    }
}

/**
 * Smart routing function - routes to charge or invoice based on payment method
 * Phase 8: Smart Button Logic
 */
export async function smartChargeOrInvoice() {
    if (window.hasPaymentMethod && window.selectedPaymentMethodId) {
        // Customer has payment method - charge directly
        if (window.adminApp && window.adminApp.chargeCustomer) {
            return await window.adminApp.chargeCustomer();
        } else {
            console.error('❌ chargeCustomer function not found');
            alert('Error: Charge function not available');
            return false;
        }
    } else {
        // No payment method - create invoice
        if (window.invoiceCustomer) {
            return await window.invoiceCustomer();
        } else {
            console.error('❌ invoiceCustomer function not found');
            alert('Error: Invoice function not available');
            return false;
        }
    }
}

// Expose to window temporarily
window.updateChargeSummary = updateChargeSummary;
window.selectPaymentMethod = selectPaymentMethod;
window.updateChargeButtonText = updateChargeButtonText;
window.smartChargeOrInvoice = smartChargeOrInvoice;
